import React, { useSyncExternalStore } from 'react';
import {
  UserCheck,
  Flag,
  TicketX,
  TicketCheck,
  Users,
  CalendarCheck,
  Clock,
  Ban,
  TrendingUp,
  type LucideIcon,
} from 'lucide-react';

/**
 * Admin activity / notification center — shared store.
 *
 * This is the in-app "activity inbox" surfaced by the header bell dropdown and
 * the full `/activity` screen (NOT the push-notification composer at
 * `/notifications`). Both views subscribe to this single store so the unread
 * count, "Mark all read", and per-row read state stay in sync.
 *
 * The backend has no admin activity-feed endpoint yet, so the list is seeded
 * with representative items. When that endpoint lands, replace `SEED_ITEMS`
 * (and `markAll`/`read`) with API calls — the components consume only the hook,
 * so nothing else has to change. Mirrors the sidebar nav counts that are
 * likewise "wired to live data in later phases".
 */

export type NotifTone = 'indigo' | 'error' | 'success' | 'warning' | 'neutral';

/** Maps a tone to its icon-chip foreground/background tokens. */
export const NOTIF_TONES: Record<NotifTone, { color: string; bg: string }> = {
  indigo: { color: 'text-indigo', bg: 'bg-indigo-tint' },
  error: { color: 'text-error', bg: 'bg-error-bg' },
  success: { color: 'text-success', bg: 'bg-success-bg' },
  warning: { color: 'text-warning', bg: 'bg-warning-bg' },
  neutral: { color: 'text-ink-2', bg: 'bg-surface-2' },
};

export type NotifCategory =
  | 'applications'
  | 'reviews'
  | 'bookings'
  | 'system'
  | 'classes'
  | 'users';

export type NotifItem = {
  id: string;
  category: NotifCategory;
  tone: NotifTone;
  icon: LucideIcon;
  /** Bucket header on the full screen ("Today" / "Earlier"). */
  group: 'Today' | 'Earlier';
  /** Relative time label. */
  time: string;
  unread: boolean;
  /** Optional inline CTA label (e.g. "Review"). */
  action?: string;
  /** Route to open when the row is activated. */
  to: string;
  /** Rich title; **bold** spans wrap the entities involved. */
  title: React.ReactNode;
};

// Seed data — representative Latio admin activity. Replace with API later.
const SEED_ITEMS: NotifItem[] = [
  {
    id: 'n1', category: 'applications', tone: 'indigo', icon: UserCheck, group: 'Today',
    time: '12m', unread: true, action: 'Review', to: '/trainer-applications',
    title: (<><b>Mateo Restrepo</b> applied to coach <b>Tenis</b> in Medellín.</>),
  },
  {
    id: 'n2', category: 'reviews', tone: 'error', icon: Flag, group: 'Today',
    time: '1h', unread: true, action: 'Review', to: '/session-reviews',
    title: (<>A review on <b>Manuela Cano</b> was flagged for moderation.</>),
  },
  {
    id: 'n3', category: 'applications', tone: 'success', icon: UserCheck, group: 'Today',
    time: '2h', unread: true, to: '/trainer-applications',
    title: (<>You approved <b>Daniela Arango</b> as a Pádel coach.</>),
  },
  {
    id: 'n4', category: 'bookings', tone: 'error', icon: TicketX, group: 'Today',
    time: '3h', unread: false, to: '/bookings',
    title: (<><b>Tomás Quintero</b> cancelled a booking for “Tennis Fundamentals”.</>),
  },
  {
    id: 'n5', category: 'system', tone: 'neutral', icon: Users, group: 'Today',
    time: '5h', unread: false, to: '/',
    title: (<><b>96 athletes</b> joined Latio this week.</>),
  },
  {
    id: 'n6', category: 'classes', tone: 'success', icon: CalendarCheck, group: 'Earlier',
    time: 'Yesterday', unread: false, to: '/sessions',
    title: (<><b>Sofía Marín</b> published “Morning Power Yoga”.</>),
  },
  {
    id: 'n7', category: 'applications', tone: 'warning', icon: Clock, group: 'Earlier',
    time: 'Yesterday', unread: false, to: '/trainer-applications',
    title: (<><b>5 applications</b> have been waiting more than 48 hours.</>),
  },
  {
    id: 'n8', category: 'reviews', tone: 'error', icon: Flag, group: 'Earlier',
    time: 'Tue', unread: false, to: '/session-reviews',
    title: (<>A review on <b>Isabela Rúa</b> was flagged — possible contact info.</>),
  },
  {
    id: 'n9', category: 'bookings', tone: 'success', icon: TicketCheck, group: 'Earlier',
    time: 'Tue', unread: false, to: '/bookings',
    title: (<><b>Trail Run · El Volador</b> reached 24 bookings.</>),
  },
  {
    id: 'n10', category: 'users', tone: 'error', icon: Ban, group: 'Earlier',
    time: 'Mon', unread: false, to: '/users',
    title: (<>You suspended <b>Manuela Cano</b> for multiple complaints.</>),
  },
  {
    id: 'n11', category: 'system', tone: 'neutral', icon: TrendingUp, group: 'Earlier',
    time: 'Mon', unread: false, to: '/',
    title: (<>Weekly GMV reached <b>$58.4M</b> — up 14% from last week.</>),
  },
];

/** Category filter chips for the full screen (in display order). */
export const NOTIF_CATEGORIES: { id: 'all' | 'unread' | NotifCategory; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'unread', label: 'Unread' },
  { id: 'applications', label: 'Applications' },
  { id: 'reviews', label: 'Reviews' },
  { id: 'bookings', label: 'Bookings' },
  { id: 'system', label: 'System' },
];

// ── external store (framework-agnostic, shared across views) ──
let items: NotifItem[] = SEED_ITEMS.slice();
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((fn) => fn());

const store = {
  subscribe(fn: () => void) {
    listeners.add(fn);
    return () => listeners.delete(fn);
  },
  getSnapshot() {
    return items;
  },
  markAll() {
    if (items.every((n) => !n.unread)) return;
    items = items.map((n) => (n.unread ? { ...n, unread: false } : n));
    emit();
  },
  read(id: string) {
    const target = items.find((n) => n.id === id);
    if (!target || !target.unread) return;
    items = items.map((n) => (n.id === id ? { ...n, unread: false } : n));
    emit();
  },
};

export function useNotificationCenter() {
  const list = useSyncExternalStore(store.subscribe, store.getSnapshot);
  return {
    items: list,
    unread: list.filter((n) => n.unread).length,
    markAll: store.markAll,
    read: store.read,
  };
}
